import { forwardRef, useId } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/utils/cn/cn.util';

import { SelectionPanel } from '../SelectionPanel/SelectionPanel';
import { SelectionTrigger } from '../SelectionTrigger/SelectionTrigger';

import { type ComboboxProps } from './Combobox.types';
import { useComboboxLogic } from './useComboboxLogic';

/**
 * One value out of a local list, with a filter and — where the list is curation
 * rather than law — a deliberate way to name a value it does not carry.
 *
 * Use it instead of `Select` whenever the list is long enough to want filtering,
 * or open enough to want the create row. `Select` stays the right control for a
 * short, genuinely closed enum, where a filter would be chrome over four rows.
 */
export const Combobox = forwardRef<HTMLButtonElement, ComboboxProps>(function Combobox(
  {
    id,
    className,
    size,
    placeholder,
    isDisabled = false,
    isReadOnly = false,
    isInvalid = false,
    onBlur,
    'aria-describedby': describedBy,
    'aria-labelledby': labelledBy,
    ...rest
  },
  ref,
) {
  const { t } = useTranslation('uiKit');
  const panelId = useId();
  const logic = useComboboxLogic({ ...rest, isDisabled, isReadOnly });

  return (
    // `min-w-0`: the trigger's summary carries `truncate`, and truncation has
    // nothing to shrink against while this wrapper sits in a grid or flex parent
    // whose implicit minimum is its CONTENT's width. A long value then pushes
    // the whole control past its cell instead of clipping — measured on the
    // country picker at 320 (P10.6e1's rule, one layer down).
    <div className={cn('flex min-w-0 flex-col', className)}>
      <SelectionTrigger
        ref={ref}
        role="combobox"
        id={id}
        panelId={panelId}
        isOpen={logic.isOpen}
        size={size}
        isDisabled={isDisabled}
        isInvalid={isInvalid}
        placeholder={placeholder ?? t('combobox.placeholder')}
        summary={logic.summary}
        onBlur={onBlur}
        aria-describedby={describedBy}
        aria-labelledby={labelledBy}
        clear={
          logic.isClearVisible ? { label: t('multiSelect.clear'), onClear: logic.clear } : undefined
        }
        onOpenChange={logic.onOpenChange}
      >
        <SelectionPanel mode="single" {...logic.panel} />
      </SelectionTrigger>
    </div>
  );
});
